import { AlertTriangle } from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { filesApi, type FsWorkspaceContext } from "../nestAPIs/files"
import { gitApi } from "../nestAPIs/git"
import { joinPath } from "./utils/paths"
import { hasImageExtension, hasPdfExtension } from "./utils/pathReference"
import { type FileBytesReader, FileBytesPreview, FileMessage } from "./FileContentViewer"

interface GitBinaryFilePreviewProps {
    workspaceId: string
    workspaceRoot: string
    worktreeId?: string
    workDir: string
    filePath: string
    treeish?: string
}

export function GitBinaryFilePreview({ workspaceId, workspaceRoot, worktreeId, workDir, filePath, treeish }: GitBinaryFilePreviewProps) {
    const previewKind = hasImageExtension(filePath) ? "image" : hasPdfExtension(filePath) ? "pdf" : null
    const [metadata, setMetadata] = useState<{ size: number; mediaType: string | null } | null>(null)
    const [error, setError] = useState<string | null>(null)
    const absolutePath = joinPath(workDir, filePath)
    const context = useMemo<FsWorkspaceContext>(
        () => ({ workspaceId, workspaceRoot, ...(worktreeId ? { worktreeId } : {}) }),
        [workspaceId, workspaceRoot, worktreeId]
    )
    const treeTarget = useMemo(
        () => ({ workspaceId, workspaceRoot, directory: workDir, ...(worktreeId ? { worktreeId } : {}), treeish: treeish ?? "", filePath }),
        [filePath, treeish, workDir, workspaceId, workspaceRoot, worktreeId]
    )
    const readFileBytes = useMemo<FileBytesReader>(
        () =>
            treeish
                ? (options) => gitApi.readFileAtTreeishBytes(treeTarget, options)
                : (options) => filesApi.readFileBytes({ kind: "workspace", path: absolutePath, context }, options),
        [absolutePath, context, treeTarget, treeish]
    )

    useEffect(() => {
        if (!previewKind) return
        const controller = new AbortController()
        setMetadata(null)
        setError(null)
        const request = treeish
            ? gitApi.describeFileAtTreeishBytes(treeTarget, { signal: controller.signal })
            : filesApi.describePath({ path: absolutePath, readContents: false, context })
        void request
            .then((result) => {
                if (controller.signal.aborted) return
                if ("exists" in result) {
                    if (!result.exists) {
                        setError("The selected revision does not contain this file.")
                        return
                    }
                    setMetadata({ size: result.sizeBytes, mediaType: result.mediaType })
                    return
                }
                if (result.type !== "file") {
                    setError(result.type === "error" ? result.message : "The working tree does not contain this file.")
                    return
                }
                setMetadata({ size: result.size, mediaType: result.mediaType ?? null })
            })
            .catch((cause: unknown) => {
                if (controller.signal.aborted) return
                setError(cause instanceof Error ? cause.message : "Binary preview failed")
            })
        return () => controller.abort()
    }, [absolutePath, context, previewKind, treeTarget, treeish])

    if (!previewKind) {
        return <div className="flex items-center justify-center py-12 text-sm text-muted">Binary file — cannot display diff</div>
    }
    if (error) {
        return <FileMessage icon={<AlertTriangle size={24} className="text-warning" />} title="Binary preview failed" detail={error} />
    }
    if (!metadata) {
        return <FileMessage title="Loading preview..." />
    }
    return <FileBytesPreview path={filePath} mediaType={metadata.mediaType} size={metadata.size} previewKind={previewKind} readFileBytes={readFileBytes} />
}
